import type { Transport, TransportAssoc } from "@ots-frontend/ots-data";
import type {
  ActivityCellFlag,
  ActivityCellTone,
  ActivityHighlightOptions,
  ActivityValidationStep,
  ActivityWarning,
} from "./activity-types";

/** Cell highlights and warning handling: WaterGo's grid rules, kept apart from how entries are built. */
const TICKET_NUMBER_KEYWORDS = ["remove", "delete", "duplicate"];

const hasText = (value?: string) => !!value && value.trim().length > 0;

const findWarning = (warnings: ActivityWarning[], type: string) => warnings.find((warning) => warning.type === type);

export const warningFlag = (warnings: ActivityWarning[], type: string, fallback: string): ActivityCellFlag | undefined => {
  const warning = findWarning(warnings, type);
  return warning && {
    rule: type.toLowerCase().replace(/_/g, "-"),
    tone: "danger",
    message: hasText(warning.message) ? warning.message : fallback,
  };
};

export const ticketNumberFlag = (transport: Transport | undefined, warnings: ActivityWarning[]): ActivityCellFlag | undefined => {
  const duplicate = findWarning(warnings, "DUPLICATE_TICKET_NUMBER");
  if (duplicate) {
    return {
      rule: "duplicate-ticket-number",
      tone: "warning",
      message: hasText(duplicate.message) ? duplicate.message : "Duplicate ticket number.",
    };
  }

  const ticketNumber = transport?.ticketNumber?.toLowerCase();
  const keyword = ticketNumber && TICKET_NUMBER_KEYWORDS.find((word) => ticketNumber.includes(word));
  return keyword
    ? {
      rule: "ticket-number-review-keyword",
      tone: "warning",
      message: `Ticket number contains the review keyword ‘${keyword}’.`,
    }
    : undefined;
};

/** Driver activity sends the second reading as `scadaQty2`; other endpoints call it `scada2Qty`. */
export const secondScadaQty = (transport: Transport): number | undefined => transport.scadaQty2 ?? transport.scada2Qty;

/** The legacy rule: a load's pickup and delivery should belong to one operator. */
export const operatorMismatchFlag = (pickup?: Transport, delivery?: Transport): ActivityCellFlag | undefined => {
  if (!pickup || !delivery) return undefined;
  const operators = new Set<string | undefined>();
  for (const transport of [pickup, delivery]) {
    if (transport.companyType === "Operator") operators.add(transport.companyId);
    if (transport.siteCompanyType === "Operator") operators.add(transport.siteCompanyId);
  }
  return operators.size > 1
    ? { rule: "operator-mismatch", tone: "danger", message: "Pickup and delivery operators do not match." }
    : undefined;
};

/** WaterGo's signed difference: how far the ticket sits above (+) or below (−) SCADA, in percent of the ticket. */
const scadaDifference = (ticket: number, scada: number) => ticket === 0 ? 0 : ((ticket - scada) / ticket) * 100;

export const scadaFlag = (
  pickup: Transport | undefined,
  delivery: Transport | undefined,
  options: ActivityHighlightOptions
): ActivityCellFlag | undefined => {
  if (!options.markScada || !pickup) return undefined;

  const readings = [pickup.scadaQty, secondScadaQty(pickup)].filter((qty): qty is number => qty != null);
  if (!readings.length) return undefined;

  const compared = options.useDriverQuantityForScada && pickup.driverQty && pickup.driverQty > 0
    ? pickup.driverQty
    : pickup.quantityDecimal;
  const high = options.scadaHighPercent ?? 5;
  const low = options.scadaLowPercent ?? -5;
  const differences = readings.map((reading) => scadaDifference(compared, reading));
  const inRange = differences.filter((difference) => difference <= high && difference >= low).length;
  // Judged on the reading closest to the ticket, as WaterGo does: over the high mark is an error, under the low one a caution.
  const closest = differences.reduce((best, difference) => Math.abs(difference) < Math.abs(best) ? difference : best);
  const tone: ActivityCellTone =
    inRange === readings.length ? "success"
      : inRange > 0 ? "warning"
        : closest > high ? "danger" : "warning";
  const uomMatches = !delivery || pickup.quantityUnitOfMeasure === delivery.quantityUnitOfMeasure;

  return {
    rule: "scada-quantity",
    tone,
    message: `SCADA quantity comparison — ticket: ${compared}, SCADA: ${readings.join(" / ")}`
      + (uomMatches ? "" : ". Pickup and delivery units of measure do not match"),
  };
};

export const demurrageFlag = (tta: TransportAssoc | undefined, options: ActivityHighlightOptions): ActivityCellFlag | undefined => {
  if (!options.markDemurrage || !tta) return undefined;

  const disabled = !!tta.pickupTransport?.disableDemurrage || !!tta.deliveryTransport?.disableDemurrage;
  const charged = (tta.objectCostRateModelList ?? []).some(
    (model) => (model.pickupDemurrageCost ?? 0) > 0 || (model.deliveryDemurrageCost ?? 0) > 0
  );
  if (disabled) {
    return { rule: "demurrage-disabled", tone: "warning", message: "Demurrage is present, but it is disabled for this ticket." };
  }
  return charged ? { rule: "demurrage", tone: "warning", message: "Demurrage is present on this ticket." } : undefined;
};

export const mergeWarnings = (...lists: (ActivityWarning[] | undefined)[]): ActivityWarning[] => {
  const seen = new Set<string>();
  return lists.flatMap((list) => list ?? []).filter((warning) => {
    const key = `${warning.type}|${warning.message}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const validationSteps = (tta?: TransportAssoc): ActivityValidationStep[] => tta
  ? [
    { id: "dispatch", label: "Dispatch", validatedDttm: tta.dispatchValidatedDttm, validatorName: tta.dispatchValidatorName },
    { id: "field", label: "Field", validatedDttm: tta.fieldValidatedDttm, validatorName: tta.fieldValidatorName },
    { id: "admin", label: "Admin", validatedDttm: tta.adminValidatedDttm, validatorName: tta.adminValidatorName },
    { id: "business", label: "Business", validatedDttm: tta.businessValidatedDttm, validatorName: tta.businessValidatorName },
  ]
  : [];
